import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiArrowLeft, FiImage, FiDownload, FiMaximize2, FiX, FiMessageCircle } from 'react-icons/fi';
import { documentsService, Document } from '../services/documents.service';
import { propertiesService, Property } from '../services/properties.service';
import { messagesService, Message } from '../services/messages.service';
import { api } from '../config/api';
import { toast } from 'react-hot-toast';
import { safeFormatDate } from '../utils/dateUtils';
import { tw, monoFont } from '../utils/terminalTheme';

const PropertyGallery: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [property, setProperty] = useState<Property | null>(null);
  const [images, setImages] = useState<Document[]>([]);
  const [messageImages, setMessageImages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<Document | null>(null);
  const [selectedMessageImage, setSelectedMessageImage] = useState<{ message: Message; mediaIndex: number } | null>(null);
  const [imageUrls, setImageUrls] = useState<Map<string, string>>(new Map());
  const [activeTab, setActiveTab] = useState<'documents' | 'messages'>('documents');

  useEffect(() => {
    if (id) {
      loadGalleryData();
    }

    return () => {
      imageUrls.forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [id]);

  const loadGalleryData = async () => {
    try {
      setIsLoading(true);

      const propertyData = await propertiesService.getProperty(parseInt(id!));
      setProperty(propertyData);

      const propertyId = parseInt(id!);
      if (isNaN(propertyId)) {
        console.error('Invalid property ID:', id);
        return;
      }

      try {
        const docs = await documentsService.getDocuments({
          property_id: propertyId,
          limit: 1000
        });

        const imageDocs = docs.filter(doc =>
          doc.mime_type?.startsWith('image/') ||
          /\.(jpg|jpeg|png|gif|bmp|tiff?)$/i.test(doc.filename)
        );

        setImages(imageDocs);
        await loadImageUrls(imageDocs);
      } catch (docError: any) {
        console.error('Failed to load documents, continuing with message media only:', docError);
        setImages([]);
      }

      try {
        const mediaMessages = await messagesService.getPropertyMediaMessages(parseInt(id!));
        setMessageImages(mediaMessages);
      } catch (error) {
        console.error('Failed to load message media:', error);
        toast.error('Failed to load message media');
      }
    } catch (error: any) {
      console.error('Error loading gallery:', error);

      if (error.response?.status === 422) {
        console.error('Validation error details:', error.response.data);
        if (error.response.data?.detail) {
          const errorMsg = Array.isArray(error.response.data.detail)
            ? error.response.data.detail[0]?.msg || 'Invalid request parameters'
            : error.response.data.detail;
          toast.error(`Failed to load gallery: ${errorMsg}`);
        } else {
          toast.error('Failed to load gallery: Invalid request parameters');
        }
      } else {
        toast.error('Failed to load gallery');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadImageUrls = async (imageDocs: Document[]) => {
    const urlMap = new Map<string, string>();

    for (const doc of imageDocs) {
      try {
        const token = localStorage.getItem('accessToken');
        const response = await api.get(`/documents/${doc.id}/download`, {
          responseType: 'blob',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const blob = new Blob([response.data], { type: doc.mime_type || 'image/jpeg' });
        const url = URL.createObjectURL(blob);
        urlMap.set(doc.id, url);
      } catch (error) {
        console.error(`Failed to load image ${doc.id}:`, error);
      }
    }

    setImageUrls(urlMap);
  };

  const handleDownload = async (doc: Document) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await api.get(`/documents/${doc.id}/download`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download image');
    }
  };

  if (isLoading) {
    return (
      <div className={tw.loadingContainer}>
        <div className={tw.loading}></div>
      </div>
    );
  }

  return (
    <div className={tw.page}>
      {/* Terminal Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold font-mono text-white" style={monoFont}>
            <span className="text-cyan-400">~/</span>properties/<span className="text-cyan-400">{property?.name || 'gallery'}</span>/gallery
            <span className="text-gray-600 text-sm font-normal ml-3">
              <span className="text-green-500">$</span> ls --images
            </span>
          </h1>
          <p className="text-gray-500 text-sm font-mono mt-1">
            {images.length} document images &middot; {messageImages.reduce((sum, m) => sum + (m.media_urls?.length || 0), 0)} message images
          </p>
        </div>
        <Link
          to={`/properties/${id}`}
          className={tw.btnSecondary + ' flex items-center gap-2'}
        >
          <FiArrowLeft className="w-3.5 h-3.5" />
          Back to Property
        </Link>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('documents')}
          className={`px-3 py-1.5 rounded-md text-xs font-mono transition-all flex items-center gap-1.5 ${
            activeTab === 'documents' ? tw.filterActive : tw.filterInactive
          }`}
        >
          <FiImage className="w-3 h-3" />
          Documents ({images.length})
        </button>
        <button
          onClick={() => setActiveTab('messages')}
          className={`px-3 py-1.5 rounded-md text-xs font-mono transition-all flex items-center gap-1.5 ${
            activeTab === 'messages' ? tw.filterActive : tw.filterInactive
          }`}
        >
          <FiMessageCircle className="w-3 h-3" />
          Messages ({messageImages.reduce((sum, m) => sum + (m.media_urls?.length || 0), 0)})
        </button>
      </div>

      {/* Gallery Grid */}
      {activeTab === 'documents' ? (
        images.length === 0 ? (
          <div className={tw.tableWrap}>
            <div className={tw.empty}>
              <FiImage className="w-12 h-12 text-gray-700 mx-auto mb-3" />
              <p className="text-white font-mono text-sm mb-1">No Images</p>
              <p className="text-gray-500 font-mono text-xs">No images have been uploaded for this property yet.</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {images.map((image) => {
            const imageUrl = imageUrls.get(image.id);
            return (
              <div key={image.id} className="relative group bg-gray-800/80 border border-gray-700/50 rounded-lg overflow-hidden hover:border-cyan-500/30 transition-colors">
                {imageUrl ? (
                  <>
                    <img
                      src={imageUrl}
                      alt={image.filename}
                      className="w-full h-48 object-cover cursor-pointer"
                      onClick={() => setSelectedImage(image)}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedImage(image);
                        }}
                        className="p-2 bg-white/20 rounded-lg mr-2 hover:bg-white/30 transition-colors"
                      >
                        <FiMaximize2 className="w-4 h-4 text-white" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(image);
                        }}
                        className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                      >
                        <FiDownload className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="w-full h-48 flex items-center justify-center bg-gray-900">
                    <div className={tw.loading + ' h-8 w-8'}></div>
                  </div>
                )}
                <div className="p-3">
                  <p className="text-white text-xs font-mono truncate">{image.filename}</p>
                  <p className="text-gray-500 text-[10px] font-mono">
                    {safeFormatDate(image.created_at)}
                  </p>
                </div>
              </div>
            );
            })}
          </div>
        )
      ) : (
        /* Message Images Tab */
        messageImages.length === 0 ? (
          <div className={tw.tableWrap}>
            <div className={tw.empty}>
              <FiMessageCircle className="w-12 h-12 text-gray-700 mx-auto mb-3" />
              <p className="text-white font-mono text-sm mb-1">No Message Images</p>
              <p className="text-gray-500 font-mono text-xs">No images have been sent via messages for this property yet.</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {messageImages.map((message) => {
              return message.media_urls?.map((mediaUrl, index) => {
                const imageUrl = mediaUrl;

                return (
                  <div key={`${message.id}-${index}`} className="relative group bg-gray-800/80 border border-gray-700/50 rounded-lg overflow-hidden hover:border-cyan-500/30 transition-colors">
                    <img
                      src={imageUrl}
                      alt={`Message from ${message.phone_number}`}
                      className="w-full h-48 object-cover cursor-pointer"
                      onClick={() => setSelectedMessageImage({ message, mediaIndex: index })}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedMessageImage({ message, mediaIndex: index });
                        }}
                        className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                      >
                        <FiMaximize2 className="w-4 h-4 text-white" />
                      </button>
                    </div>
                    <div className="p-3">
                      <p className="text-white text-xs font-mono truncate">
                        {message.direction === 'incoming' ? 'From' : 'To'}: {message.contact?.name || message.phone_number}
                      </p>
                      <p className="text-gray-500 text-[10px] font-mono">
                        {safeFormatDate(message.timestamp, 'MMM d, yyyy h:mm a')}
                      </p>
                    </div>
                  </div>
                );
              });
            })}
          </div>
        )
      )}

      {/* Lightbox */}
      {(selectedImage || selectedMessageImage) && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => {
            setSelectedImage(null);
            setSelectedMessageImage(null);
          }}
        >
          <div className="relative max-w-7xl max-h-[90vh]">
            <button
              onClick={() => {
                setSelectedImage(null);
                setSelectedMessageImage(null);
              }}
              className="absolute top-4 right-4 p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors z-10"
            >
              <FiX className="w-5 h-5 text-white" />
            </button>
            {selectedImage ? (
              <>
                <img
                  src={imageUrls.get(selectedImage.id)}
                  alt={selectedImage.filename}
                  className="max-w-full max-h-[90vh] object-contain"
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="absolute bottom-4 left-4 right-4 bg-black/70 p-4 rounded-lg">
                  <p className="text-white font-mono text-sm">{selectedImage.filename}</p>
                  <p className="text-gray-400 font-mono text-xs">
                    {safeFormatDate(selectedImage.created_at, 'MMMM d, yyyy h:mm a')}
                  </p>
                </div>
              </>
            ) : selectedMessageImage ? (
              <>
                <img
                  src={selectedMessageImage.message.media_urls![selectedMessageImage.mediaIndex]}
                  alt={`Message from ${selectedMessageImage.message.phone_number}`}
                  className="max-w-full max-h-[90vh] object-contain"
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="absolute bottom-4 left-4 right-4 bg-black/70 p-4 rounded-lg">
                  <p className="text-white font-mono text-sm">
                    {selectedMessageImage.message.direction === 'incoming' ? 'From' : 'To'}: {selectedMessageImage.message.contact?.name || selectedMessageImage.message.phone_number}
                  </p>
                  <p className="text-gray-400 font-mono text-xs">
                    {safeFormatDate(selectedMessageImage.message.timestamp, 'MMMM d, yyyy h:mm a')}
                  </p>
                  {selectedMessageImage.message.body && (
                    <p className="text-gray-400 font-mono text-xs mt-2 italic">
                      "{selectedMessageImage.message.body}"
                    </p>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyGallery;
